import { Button, cn, Popover, PopoverContent, PopoverTrigger, PseudoInputButton, PseudoInputIcon, PseudoInputLabel } from '@1trading/react';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { formatUtcMonth } from '@/utils/format';

const MONTHS = Array.from({ length: 12 }, (_, index) => index + 1);

const toValue = (year: number, month: number) => `${year}-${String(month).padStart(2, '0')}`;
const yearOf = (value: string) => Number(value.slice(0, 4));

interface MonthPickerProps {
  id?: string;
  /** `YYYY-MM`, or blank for no month */
  value: string;
  onChange: (month: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Last `YYYY-MM` that may be picked; later months are greyed out */
  max?: string;
  'aria-invalid'?: boolean;
  className?: string;
}

/** `DatePicker`'s month sibling: the same trigger, opening on a year of months instead of a calendar. */
export const MonthPicker = ({ id, value, onChange, placeholder = 'Pick a month', disabled, max, 'aria-invalid': ariaInvalid, className }: MonthPickerProps) => {
  const [open, setOpen] = useState(false);
  const shownYear = () => (value ? yearOf(value) : max ? yearOf(max) : new Date().getUTCFullYear());
  const [year, setYear] = useState(shownYear);
  const label = value ? formatUtcMonth(Date.UTC(yearOf(value), Number(value.slice(5, 7)) - 1, 1)) : placeholder;
  const pick = (month: string) => {
    onChange(month);
    setOpen(false);
  };

  return (
    <Popover
      onOpenChange={(next) => {
        // Each opening starts on the picked month's year.
        if (next) setYear(shownYear());
        setOpen(next);
      }}
      open={open}
    >
      <PopoverTrigger asChild>
        <PseudoInputButton aria-invalid={ariaInvalid} className={cn('h-[var(--input-height)] border-input bg-card text-card-foreground', className)} disabled={disabled} id={id}>
          <PseudoInputLabel className={cn(!value && 'text-muted-foreground')}>{label}</PseudoInputLabel>
          <PseudoInputIcon>
            <CalendarDays />
          </PseudoInputIcon>
        </PseudoInputButton>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-3">
        <div className="mb-2 flex items-center justify-between">
          <Button aria-label="Previous year" className="size-7" onClick={() => setYear(year - 1)} size="icon" type="button" variant="ghost">
            <ChevronLeft className="size-4" />
          </Button>
          <span className="text-sm font-medium">{year}</span>
          <Button
            aria-label="Next year"
            className="size-7"
            disabled={max !== undefined && year >= yearOf(max)}
            onClick={() => setYear(year + 1)}
            size="icon"
            type="button"
            variant="ghost"
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-1">
          {MONTHS.map((month) => {
            const candidate = toValue(year, month);
            return (
              <Button
                aria-pressed={candidate === value}
                className="h-8 font-normal"
                disabled={max !== undefined && candidate > max}
                key={candidate}
                onClick={() => pick(candidate)}
                size="sm"
                type="button"
                variant={candidate === value ? 'primary' : 'ghost'}
              >
                {formatUtcMonth(Date.UTC(year, month - 1, 1), false)}
              </Button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
MonthPicker.displayName = 'MonthPicker';
